import {
  Cleaner,
  Driver,
  Lady,
  Manager,
  Mechanic,
  Parking,
  PetrolPump,
  Route,
  staffApi,
  Vehicle,
} from "../../../services/api";
import { projectMasterDataApi } from "./projectMasterDataApiService";

export type SearchableEntity =
  | "DRIVER"
  | "CLEANER"
  | "LADY"
  | "MANAGER"
  | "PETROL_PUMP"
  | "PARKING"
  | "MECHANIC"
  | "VEHICLE"
  | "ROUTE";

export interface SearchResult {
  data: any[];
  isLoading: boolean;
  error: string | null;
}

export interface SearchHandler {
  search: (
    entity: SearchableEntity,
    projectId: string,
    allocationId?: string
  ) => Promise<any[]>;
  minSearchLength: number;
  mapResult: (item: any) => any;
}

const minSearchLength = 0;

const searchHandlers: Record<SearchableEntity, SearchHandler> = {
  DRIVER: {
    search: projectMasterDataApi.getProjectMasterDataByType,
    minSearchLength: minSearchLength,
    mapResult: (driver: Driver) => ({
      id: driver.id,
      name: driver.name,
      contactNumber: driver.contactNumber,
      ...(driver.aadharNumber && { aadharNumber: driver.aadharNumber }),
      ...(driver.employmentType && { employmentType: driver.employmentType }),
      ...(driver.address && { address: driver.address }),
      ...(driver.status && { status: driver.status }),
      ...(driver.joiningDate && { joiningDate: driver.joiningDate }),
      ...(driver.salary && { salary: driver.salary }),
      ...(driver.drivingLicense && { drivingLicense: driver.drivingLicense }),
    }),
  },
  CLEANER: {
    search: projectMasterDataApi.getProjectMasterDataByType,
    minSearchLength: minSearchLength,
    mapResult: (cleaner: Cleaner) => ({
      id: cleaner.id,
      name: cleaner.name,
      contactNumber: cleaner.contactNumber,
      ...(cleaner.aadharNumber && { aadharNumber: cleaner.aadharNumber }),
      ...(cleaner.employmentType && { employmentType: cleaner.employmentType }),
      ...(cleaner.address && { address: cleaner.address }),
      ...(cleaner.status && { status: cleaner.status }),
      ...(cleaner.joiningDate && { joiningDate: cleaner.joiningDate }),
      ...(cleaner.salary && { salary: cleaner.salary }),
    }),
  },
  LADY: {
    search: projectMasterDataApi.getProjectMasterDataByType,
    minSearchLength: minSearchLength,
    mapResult: (lady: Lady) => ({
      id: lady.id,
      name: lady.name,
      contactNumber: lady.contactNumber,
      ...(lady.aadharNumber && { aadharNumber: lady.aadharNumber }),
      ...(lady.employmentType && { employmentType: lady.employmentType }),
      ...(lady.address && { address: lady.address }),
      ...(lady.status && { status: lady.status }),
      ...(lady.joiningDate && { joiningDate: lady.joiningDate }),
      ...(lady.salary && { salary: lady.salary }),
    }),
  },
  MANAGER: {
    search: projectMasterDataApi.getProjectMasterDataByType,
    minSearchLength: minSearchLength,
    mapResult: (manager: Manager) => ({
      id: manager.id,
      name: manager.name,
      contactNumber: manager.contactNumber,
      ...(manager.aadharNumber && { aadharNumber: manager.aadharNumber }),
      ...(manager.employmentType && { employmentType: manager.employmentType }),
      ...(manager.address && { address: manager.address }),
      ...(manager.status && { status: manager.status }),
      ...(manager.joiningDate && { joiningDate: manager.joiningDate }),
      ...(manager.salary && { salary: manager.salary }),
    }),
  },
  PETROL_PUMP: {
    search: projectMasterDataApi.getProjectMasterDataByType,
    minSearchLength: minSearchLength,
    mapResult: (pump: PetrolPump) => ({
      id: pump.id,
      petrolPumpName: pump.petrolPumpName,
      primaryContactNumber: pump.primaryContactNumber,
      location: pump.location,
      providerName: pump.providerName,
    }),
  },
  PARKING: {
    search: projectMasterDataApi.getProjectMasterDataByType,
    minSearchLength: minSearchLength,
    mapResult: (parking: Parking) => ({
      id: parking.id,
      name: parking.name,
      contactNumber: parking.contactNumber,
      location: parking.location,
    }),
  },
  MECHANIC: {
    search: projectMasterDataApi.getProjectMasterDataByType,
    minSearchLength: minSearchLength,
    mapResult: (mechanic: Mechanic) => ({
      id: mechanic.id,
      name: mechanic.name,
      phoneNumber: mechanic.phoneNumber,
      location: mechanic.location,
    }),
  },
  VEHICLE: {
    search: projectMasterDataApi.getProjectMasterDataByType,
    minSearchLength: minSearchLength,
    mapResult: (vehicle: Vehicle) => ({
      id: vehicle.id,
      status: vehicle.status,
      vehicleNumber: vehicle.vehicleNumber,
      ...(vehicle.vehicleType && { vehicleType: vehicle.vehicleType }),
      ...(vehicle.acquisitionType && {
        acquisitionType: vehicle.acquisitionType,
      }),
      ...(vehicle.chassisNumber && { chassisNumber: vehicle.chassisNumber }),
      ...(vehicle.engineNumber && { engineNumber: vehicle.engineNumber }),
      ...(vehicle.makeYear && { makeYear: vehicle.makeYear }),
      ...(vehicle.model && { model: vehicle.model }),
      ...(vehicle.color && { color: vehicle.color }),
      ...(vehicle.bodyType && { bodyType: vehicle.bodyType }),
      ...(vehicle.configuration && { configuration: vehicle.configuration }),
      ...(vehicle.fuelType && { fuelType: vehicle.fuelType }),
      ...(vehicle.seatingCapacity && {
        seatingCapacity: vehicle.seatingCapacity,
      }),
      ...(vehicle.isAC && { isAC: vehicle.isAC }),
    }),
  },
  ROUTE: {
    search: (entity: SearchableEntity, projectId: string) =>
      projectMasterDataApi.getRoutesWithSchedules(projectId),
    minSearchLength: minSearchLength,
    mapResult: (route: any) => ({
      id: route.id,
      fromLocation: route.fromLocation,
      toLocation: route.toLocation,
      routeManagerName: route.routeManagerName,
      routeManagerNumber: route.routeManagerNumber,
      ...(route.stops && { stops: route.stops }),
      ...(route.schedules && {
        schedules: route.schedules,
      }),
    }),
  },
};

export class SearchService {
  private static instance: SearchService;
  private searchResults: Map<string, SearchResult> = new Map();

  private constructor() {}

  static getInstance(): SearchService {
    if (!SearchService.instance) {
      SearchService.instance = new SearchService();
    }
    return SearchService.instance;
  }

  async search(
    entity: SearchableEntity,
    searchTerm: string,
    onResults: (results: any[]) => void,
    onError: (error: string) => void,
    onLoading: (loading: boolean) => void,
    projectId: string,
    allocationId?: string
  ): Promise<void> {
    const handler = searchHandlers[entity];

    if (!handler) {
      onError(`No search handler found for entity: ${entity}`);
      return;
    }

    try {
      onLoading(true);
      const results = await handler.search(entity, projectId, allocationId);
      const mappedResults = results.map(handler.mapResult);
      onResults(mappedResults);
    } catch (error) {
      onError(`Failed to search ${entity.toLowerCase()}`);
      console.error(`Error searching ${entity}:`, error);
    } finally {
      onLoading(false);
    }
  }

  getSearchResult(entity: SearchableEntity): SearchResult {
    return (
      this.searchResults.get(entity) || {
        data: [],
        isLoading: false,
        error: null,
      }
    );
  }

  setSearchResult(entity: SearchableEntity, result: SearchResult): void {
    this.searchResults.set(entity, result);
  }

  clearSearchResult(entity: SearchableEntity): void {
    this.searchResults.delete(entity);
  }
}

export default SearchService.getInstance();
